import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  LineChart,
  Line,
  AreaChart,
  Area,
  CartesianGrid,
  ReferenceLine,
} from 'recharts';
import { formatChartDate, truncateLabel } from '@/utils/formatters';
import { CHART_COLORS, WATERFALL_COLORS } from '@/lib/theme';

// =============================================================================
// CHART AXIS GUIDELINES
// =============================================================================
// - Axis labels should be HORIZONTAL (angle: 0) for readability
// - Use tickFormatter with truncateLabel() to prevent overlap
// - Default max label length: 8 characters
// - For date axes, use formatChartDate() for consistent formatting
// =============================================================================

// =============================================================================
// DONUT CHART
// =============================================================================

interface DonutChartData {
  name: string;
  value: number;
  color?: string;
}

interface DonutChartProps {
  data: DonutChartData[];
  title?: string;
  centerLabel?: string;
  centerValue?: string;
  height?: number;
  showLegend?: boolean;
  colors?: string[];
  className?: string;
}

// Chart colors - imported from theme tokens (GAP-UX-002)
// See: src/lib/theme.ts for color definitions
const DEFAULT_COLORS: string[] = [...CHART_COLORS];

export function DonutChart({
  data,
  title,
  centerLabel,
  centerValue,
  height = 200,
  showLegend = true,
  colors = DEFAULT_COLORS,
  className = '',
}: DonutChartProps) {
  const total = data.reduce((acc, item) => acc + item.value, 0);

  return (
    <div className={className}>
      {title && (
        <h4 className="text-sm font-medium text-text-secondary mb-3">{title}</h4>
      )}
      <div className="relative" style={{ height }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              innerRadius="60%"
              outerRadius="85%"
              paddingAngle={2}
              dataKey="value"
            >
              {data.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={entry.color || colors[index % colors.length]}
                />
              ))}
            </Pie>
            <Tooltip content={<CustomTooltip />} />
          </PieChart>
        </ResponsiveContainer>

        {/* Center Label */}
        {(centerLabel || centerValue) && (
          <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
            {centerValue && (
              <span className="text-2xl font-bold text-white">{centerValue}</span>
            )}
            {centerLabel && (
              <span className="text-xs text-text-secondary">{centerLabel}</span>
            )}
          </div>
        )}
      </div>

      {/* Legend */}
      {showLegend && (
        <div className="flex flex-wrap justify-center gap-x-4 gap-y-2 mt-4">
          {data.map((entry, index) => (
            <div key={index} className="flex items-center gap-1.5">
              <span
                className="w-2.5 h-2.5 rounded-full"
                style={{ backgroundColor: entry.color || colors[index % colors.length] }}
              />
              <span className="text-xs text-text-secondary">
                {entry.name}: {((entry.value / total) * 100).toFixed(1)}%
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// =============================================================================
// HORIZONTAL BAR CHART
// =============================================================================

interface HorizontalBarData {
  name: string;
  value: number;
  secondaryValue?: number;
  color?: string;
  icon?: string;
}

interface HorizontalBarChartProps {
  data: HorizontalBarData[];
  title?: string;
  valueLabel?: string;
  secondaryLabel?: string;
  height?: number;
  showRank?: boolean;
  maxValue?: number;
  className?: string;
}

export function HorizontalBarChart({
  data,
  title,
  valueLabel = 'Value',
  secondaryLabel,
  height,
  showRank = true,
  maxValue,
  className = '',
}: HorizontalBarChartProps) {
  const max = maxValue || Math.max(...data.map((d) => d.value));

  return (
    <div className={className}>
      {title && (
        <h4 className="text-sm font-medium text-white mb-4">{title}</h4>
      )}
      <div className="space-y-3">
        {data.map((item, index) => {
          const percentage = (item.value / max) * 100;
          const isLeader = index === 0;

          return (
            <div key={item.name} className="group">
              <div className="flex items-center justify-between mb-1.5">
                <div className="flex items-center gap-2">
                  {showRank && (
                    <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold ${
                      isLeader ? 'bg-warning-500/30 text-warning-300' : 'bg-surface-600 text-text-secondary'
                    }`}>
                      {isLeader ? '🏆' : index + 1}
                    </span>
                  )}
                  {item.icon && <span className="text-sm">{item.icon}</span>}
                  <span className="text-sm font-medium text-white">{item.name}</span>
                </div>
                <div className="flex items-center gap-4 text-sm">
                  {item.secondaryValue !== undefined && secondaryLabel && (
                    <span className="text-text-tertiary">
                      {secondaryLabel}: {item.secondaryValue}%
                    </span>
                  )}
                  <span className="font-semibold text-primary-400">{item.value.toFixed(1)}%</span>
                </div>
              </div>
              <div className="h-3 bg-surface-600 rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-500"
                  style={{
                    width: `${percentage}%`,
                    backgroundColor: item.color || DEFAULT_COLORS[index % DEFAULT_COLORS.length],
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// =============================================================================
// LINE CHART
// =============================================================================

interface LineChartData {
  name: string;
  [key: string]: string | number;
}

interface LineChartProps {
  data: LineChartData[];
  lines: Array<{
    dataKey: string;
    name: string;
    color?: string;
    dashed?: boolean;
  }>;
  title?: string;
  height?: number;
  showGrid?: boolean;
  showLegend?: boolean;
  className?: string;
}

export function SimpleLineChart({
  data,
  lines,
  title,
  height = 250,
  showGrid = true,
  showLegend = true,
  className = '',
}: LineChartProps) {
  return (
    <div className={className}>
      {title && (
        <h4 className="text-sm font-medium text-white mb-4">{title}</h4>
      )}
      <ResponsiveContainer width="100%" height={height}>
        <LineChart data={data} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
          {showGrid && <CartesianGrid strokeDasharray="3 3" stroke="#334155" />}
          <XAxis
            dataKey="name"
            tick={{ fill: '#94a3b8', fontSize: 12 }}
            axisLine={{ stroke: '#475569' }}
          />
          <YAxis
            tick={{ fill: '#94a3b8', fontSize: 12 }}
            axisLine={{ stroke: '#475569' }}
          />
          <Tooltip content={<CustomTooltip />} />
          {showLegend && (
            <Legend
              wrapperStyle={{ paddingTop: 10 }}
              formatter={(value) => <span className="text-text-secondary text-sm">{value}</span>}
            />
          )}
          {lines.map((line, index) => (
            <Line
              key={line.dataKey}
              type="monotone"
              dataKey={line.dataKey}
              name={line.name}
              stroke={line.color || DEFAULT_COLORS[index % DEFAULT_COLORS.length]}
              strokeWidth={2}
              strokeDasharray={line.dashed ? '5 5' : undefined}
              dot={{ fill: line.color || DEFAULT_COLORS[index % DEFAULT_COLORS.length], strokeWidth: 0, r: 4 }}
              activeDot={{ r: 6, strokeWidth: 0 }}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

// =============================================================================
// AREA CHART
// =============================================================================

export function SimpleAreaChart({
  data,
  lines,
  title,
  height = 250,
  showGrid = true,
  className = '',
}: LineChartProps) {
  return (
    <div className={className}>
      {title && (
        <h4 className="text-sm font-medium text-white mb-4">{title}</h4>
      )}
      <ResponsiveContainer width="100%" height={height}>
        <AreaChart data={data} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
          {showGrid && <CartesianGrid strokeDasharray="3 3" stroke="#334155" />}
          <XAxis
            dataKey="name"
            tick={{ fill: '#94a3b8', fontSize: 12 }}
            axisLine={{ stroke: '#475569' }}
          />
          <YAxis
            tick={{ fill: '#94a3b8', fontSize: 12 }}
            axisLine={{ stroke: '#475569' }}
          />
          <Tooltip content={<CustomTooltip />} />
          {lines.map((line, index) => (
            <Area
              key={line.dataKey}
              type="monotone"
              dataKey={line.dataKey}
              name={line.name}
              stroke={line.color || DEFAULT_COLORS[index % DEFAULT_COLORS.length]}
              fill={line.color || DEFAULT_COLORS[index % DEFAULT_COLORS.length]}
              fillOpacity={0.2}
              strokeWidth={2}
            />
          ))}
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

// =============================================================================
// SIMPLE BAR CHART
// =============================================================================

interface BarChartProps {
  data: Array<{ name: string; value: number; color?: string }>;
  title?: string;
  height?: number;
  showGrid?: boolean;
  horizontal?: boolean;
  className?: string;
}

export function SimpleBarChart({
  data,
  title,
  height = 250,
  showGrid = true,
  className = '',
}: BarChartProps) {
  return (
    <div className={className}>
      {title && (
        <h4 className="text-sm font-medium text-white mb-4">{title}</h4>
      )}
      <ResponsiveContainer width="100%" height={height}>
        <BarChart data={data} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
          {showGrid && <CartesianGrid strokeDasharray="3 3" stroke="#334155" />}
          <XAxis
            dataKey="name"
            tick={{ fill: '#94a3b8', fontSize: 12 }}
            axisLine={{ stroke: '#475569' }}
          />
          <YAxis
            tick={{ fill: '#94a3b8', fontSize: 12 }}
            axisLine={{ stroke: '#475569' }}
          />
          <Tooltip content={<CustomTooltip />} />
          <Bar dataKey="value" radius={[4, 4, 0, 0]}>
            {data.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={entry.color || DEFAULT_COLORS[index % DEFAULT_COLORS.length]}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

// =============================================================================
// WATERFALL CHART (for decomposition)
// =============================================================================
// Shows performance change vs prior period
// Green = Growth Factors, Red = Decline Factors, Blue = Net Result
// =============================================================================

interface WaterfallData {
  name: string;
  value: number;
  isTotal?: boolean;
  tooltip?: string; // Optional explanation of what drove this change
}

interface WaterfallChartProps {
  data: WaterfallData[];
  title?: string;
  subtitle?: string; // e.g., "Performance Change vs. Prior Year"
  height?: number;
  showLegend?: boolean;
  className?: string;
}

export function WaterfallChart({
  data,
  title,
  subtitle,
  height = 300,
  showLegend = true,
  className = '',
}: WaterfallChartProps) {
  // Calculate running total for waterfall positioning
  let runningTotal = 0;
  const processedData = data.map((item) => {
    if (item.isTotal) {
      return {
        name: item.name,
        value: item.value,
        base: 0,
        fill: '#38bdf8', // Sky Blue - Net Result
        tooltip: item.tooltip,
        originalValue: item.value,
      };
    }

    const base = runningTotal;
    runningTotal += item.value;

    return {
      name: item.name,
      value: Math.abs(item.value),
      base: item.value >= 0 ? base : base + item.value,
      fill: item.value >= 0 ? WATERFALL_COLORS.positive : WATERFALL_COLORS.negative,
      tooltip: item.tooltip,
      originalValue: item.value,
    };
  });

  return (
    <div className={className}>
      {/* Header with title and subtitle */}
      {(title || subtitle) && (
        <div className="mb-4">
          {title && (
            <h4 className="text-sm font-medium text-white">{title}</h4>
          )}
          {subtitle && (
            <p className="text-xs text-text-tertiary mt-0.5">{subtitle}</p>
          )}
        </div>
      )}

      {/* Legend - explains color coding */}
      {showLegend && (
        <div className="flex items-center gap-4 mb-3 text-xs text-text-secondary">
          <span className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded" style={{ backgroundColor: WATERFALL_COLORS.positive }} />
            Growth Factors
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded" style={{ backgroundColor: WATERFALL_COLORS.negative }} />
            Decline Factors
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded" style={{ backgroundColor: WATERFALL_COLORS.result }} />
            Net Result
          </span>
        </div>
      )}

      <ResponsiveContainer width="100%" height={height}>
        <BarChart data={processedData} margin={{ top: 20, right: 20, bottom: 40, left: 20 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
          <XAxis
            dataKey="name"
            tick={{ fill: '#94a3b8', fontSize: 11 }}
            axisLine={{ stroke: '#475569' }}
            interval={0}
            angle={0}
            textAnchor="middle"
            tickFormatter={(value) => truncateLabel(value, 10)}
          />
          <YAxis
            tick={{ fill: '#94a3b8', fontSize: 12 }}
            axisLine={{ stroke: '#475569' }}
          />
          <ReferenceLine y={0} stroke="#475569" />
          <Tooltip content={<WaterfallTooltip />} />
          <Bar dataKey="base" stackId="stack" fill="transparent" />
          <Bar dataKey="value" stackId="stack" radius={[4, 4, 0, 0]}>
            {processedData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.fill} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

// Custom tooltip for waterfall chart with explanation
function WaterfallTooltip({ active, payload, label }: any) {
  if (!active || !payload || !payload.length) return null;

  const data = payload[0]?.payload;
  const originalValue = data?.originalValue;
  const tooltip = data?.tooltip;
  const isPositive = originalValue >= 0;

  return (
    <div className="bg-surface-800 border border-surface-400/30 rounded-lg px-3 py-2 shadow-xl max-w-xs">
      <p className="text-xs font-medium text-white mb-1">{label}</p>
      <p className={`text-sm font-semibold ${isPositive ? 'text-success-400' : 'text-danger-400'}`}>
        {isPositive ? '+' : ''}{originalValue?.toFixed(1)}
      </p>
      {tooltip && (
        <p className="text-xs text-text-secondary mt-1 border-t border-surface-600 pt-1">
          {tooltip}
        </p>
      )}
    </div>
  );
}

// =============================================================================
// MARKET TREND CHART (Line chart for time series)
// =============================================================================
// Use this instead of bar chart for showing trends over time
// =============================================================================

interface MarketTrendData {
  period: string; // e.g., "2023-Q1", "2024-01"
  value: number;
}

interface MarketTrendChartProps {
  data: MarketTrendData[];
  title?: string;
  height?: number;
  valueLabel?: string;
  valueSuffix?: string; // e.g., "M", "B"
  showArea?: boolean;
  highlightLast?: boolean;
  className?: string;
}

export function MarketTrendChart({
  data,
  title,
  height = 200,
  valueLabel = 'Value',
  valueSuffix = '',
  showArea = true,
  highlightLast = true,
  className = '',
}: MarketTrendChartProps) {
  // Transform data for chart
  const chartData = data.map((d, index) => ({
    name: formatChartDate(d.period),
    value: d.value,
    isLast: highlightLast && index === data.length - 1,
  }));

  // Calculate start, current, and change
  const startValue = data[0]?.value || 0;
  const currentValue = data[data.length - 1]?.value || 0;
  const changePercent = startValue > 0 ? ((currentValue - startValue) / startValue) * 100 : 0;
  const isPositive = changePercent >= 0;

  return (
    <div className={className}>
      {title && (
        <h4 className="text-xs font-medium text-text-secondary uppercase tracking-wider mb-3">
          {title}
        </h4>
      )}

      <ResponsiveContainer width="100%" height={height}>
        {showArea ? (
          <AreaChart data={chartData} margin={{ top: 10, right: 10, bottom: 20, left: 10 }}>
            <defs>
              <linearGradient id="trendGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#38bdf8" stopOpacity={0.3} />
                <stop offset="95%" stopColor="#38bdf8" stopOpacity={0} />
              </linearGradient>
            </defs>
            <XAxis
              dataKey="name"
              tick={{ fill: '#64748b', fontSize: 10 }}
              axisLine={{ stroke: '#334155' }}
              tickLine={false}
              angle={0}
              textAnchor="middle"
              interval="preserveStartEnd"
            />
            <YAxis hide />
            <Tooltip content={<TrendTooltip valueSuffix={valueSuffix} valueLabel={valueLabel} />} />
            <Area
              type="monotone"
              dataKey="value"
              stroke="#38bdf8"
              strokeWidth={2}
              fill="url(#trendGradient)"
              dot={false}
              activeDot={{ r: 6, fill: '#38bdf8', strokeWidth: 0 }}
            />
            {/* Highlight last point */}
            {highlightLast && (
              <Area
                type="monotone"
                dataKey="value"
                stroke="transparent"
                fill="transparent"
                dot={(props: any) => {
                  if (props.index === chartData.length - 1) {
                    return (
                      <circle
                        cx={props.cx}
                        cy={props.cy}
                        r={6}
                        fill="#38bdf8"
                        stroke="#0e7490"
                        strokeWidth={2}
                      />
                    );
                  }
                  return <circle r={0} />;
                }}
              />
            )}
          </AreaChart>
        ) : (
          <LineChart data={chartData} margin={{ top: 10, right: 10, bottom: 20, left: 10 }}>
            <XAxis
              dataKey="name"
              tick={{ fill: '#64748b', fontSize: 10 }}
              axisLine={{ stroke: '#334155' }}
              tickLine={false}
              angle={0}
              textAnchor="middle"
              interval="preserveStartEnd"
            />
            <YAxis hide />
            <Tooltip content={<TrendTooltip valueSuffix={valueSuffix} valueLabel={valueLabel} />} />
            <Line
              type="monotone"
              dataKey="value"
              stroke="#38bdf8"
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 6, fill: '#38bdf8', strokeWidth: 0 }}
            />
          </LineChart>
        )}
      </ResponsiveContainer>

      {/* Summary footer */}
      <div className="flex justify-between items-center mt-2 text-sm">
        <div>
          <span className="text-text-tertiary">Start</span>
          <div className="font-semibold text-white">${startValue}{valueSuffix}</div>
        </div>
        <div className="text-center">
          <span className="text-text-tertiary">Current</span>
          <div className="font-semibold text-primary-400">${currentValue}{valueSuffix}</div>
        </div>
        <div className="text-right">
          <span className="text-text-tertiary">Change</span>
          <div className={`font-semibold ${isPositive ? 'text-success-400' : 'text-danger-400'}`}>
            {isPositive ? '+' : ''}{changePercent.toFixed(1)}%
          </div>
        </div>
      </div>
    </div>
  );
}

// Custom tooltip for trend chart
function TrendTooltip({ active, payload, label, valueSuffix, valueLabel }: any) {
  if (!active || !payload || !payload.length) return null;

  return (
    <div className="bg-surface-800 border border-surface-400/30 rounded-lg px-3 py-2 shadow-xl">
      <p className="text-xs text-text-secondary mb-1">{label}</p>
      <p className="text-sm font-semibold text-white">
        {valueLabel}: ${payload[0]?.value?.toFixed(0)}{valueSuffix}
      </p>
    </div>
  );
}

// =============================================================================
// CUSTOM TOOLTIP
// =============================================================================

function CustomTooltip({ active, payload, label }: any) {
  if (!active || !payload || !payload.length) return null;

  return (
    <div className="bg-surface-800 border border-surface-400/30 rounded-lg px-3 py-2 shadow-xl">
      {label && <p className="text-xs text-text-secondary mb-1">{label}</p>}
      {payload.map((entry: any, index: number) => (
        <div key={index} className="flex items-center gap-2">
          <span
            className="w-2 h-2 rounded-full"
            style={{ backgroundColor: entry.color }}
          />
          <span className="text-sm text-white">
            {entry.name}: {typeof entry.value === 'number' ? entry.value.toFixed(1) : entry.value}
          </span>
        </div>
      ))}
    </div>
  );
}

// =============================================================================
// PROGRESS RING (Mini donut for single values)
// =============================================================================

export function ProgressRing({
  value,
  max = 100,
  size = 60,
  strokeWidth = 6,
  color = '#38bdf8', // Sky Blue
  label,
  className = '',
}: {
  value: number;
  max?: number;
  size?: number;
  strokeWidth?: number;
  color?: string;
  label?: string;
  className?: string;
}) {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const percentage = Math.min(value / max, 1);
  const offset = circumference - percentage * circumference;

  return (
    <div className={`relative inline-flex items-center justify-center ${className}`}>
      <svg width={size} height={size} className="transform -rotate-90">
        {/* Background circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="#334155"
          strokeWidth={strokeWidth}
        />
        {/* Progress circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          className="transition-all duration-500"
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-sm font-bold text-white">{Math.round(percentage * 100)}%</span>
        {label && <span className="text-[10px] text-text-tertiary">{label}</span>}
      </div>
    </div>
  );
}
